import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch, RootState } from '../../../../store/store';
import Table from '../../../../components/Table/Table';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import SpinnerLoader from '../../../../components/SpinnerLoader/SpinnerLoader';
import Modal from '../../../../components/Modal/Modal';
import { generatePath } from 'react-router-dom';
import { IActionsDropdown, IActionsTag } from '../../../../types/tableDropdown';
import IconEditOutlined from '../../../../components/Icons/IconEditOutlined/IconEditOutlined';
import IconBin from '../../../../components/Icons/IconBin/IconBin';
import { ElementSize } from '../../../../types/element-size';
import CycleSettings from '../../../../enums/cycle-setting.enum';
import { IRecipeEditable, IPreHeatRecipeEditable } from '../../../../types/recipes-types';
import { editRecipeFullPath, RECIPE_PATHS } from '../../constants';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../../../components/Tooltip';
import InputControl from '../../../../components/InputControl/InputControl';

type Recipe =
  | ({ mode: CycleSettings.PostWeld } & IRecipeEditable & { id: string })
  | ({ mode: CycleSettings.PreHeat } & IPreHeatRecipeEditable & { id: string });

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const RecipesView: React.FC<ComponentProps> = ({ getRecipes, removeRecipe, recipes = [] }) => {
  const [deleteRecipeId, setDeleteRecipeId] = useState<string | null>(null);
  const [deleteRecipeType, setDeleteRecipeType] = useState<CycleSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onCreateClickHandler = useCallback(() => {
    navigate(RECIPE_PATHS.CREATE);
  }, [navigate]);

  const onEditClickHandler = useCallback(
    (id: string) => {
      const path = generatePath(editRecipeFullPath, { id });
      navigate(path);
    },
    [navigate],
  );

  const isPostWeld = (recipe: Recipe): recipe is Extract<Recipe, { mode: CycleSettings.PostWeld }> => {
    return recipe.mode === CycleSettings.PostWeld;
  };

  const isPreHeat = (recipe: Recipe): recipe is Extract<Recipe, { mode: CycleSettings.PreHeat }> => {
    return recipe.mode === CycleSettings.PreHeat;
  };

  const postWeldRecipes = useMemo(() => {
    const filtered = recipes?.filter(isPostWeld) || [];
    if (searchTerm) {
      return filtered.filter((recipe) => recipe.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return filtered;
  }, [recipes, searchTerm]);

  const preHeatRecipes = useMemo(() => {
    const filtered = recipes?.filter(isPreHeat) || [];
    if (searchTerm) {
      return filtered.filter((recipe) => recipe.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return filtered;
  }, [recipes, searchTerm]);

  const postWeldViewValues = (postWeldRecipes || []).map((item, idx) => ({
    Name: item?.name,
    id: item?.id,
    idx,
    type: CycleSettings.PostWeld,
  }));

  const preHeatViewValues = (preHeatRecipes || []).map((item, idx) => ({
    id: item?.id,
    idx,
    name: item?.name,
    'rate (°C/h)': item?.rate,
    'finalTemp (°C)': item?.finalTemp,
    'soakUpper (°C)': item?.soakUpper,
    'soakLower (°C)': item?.soakLower,
    type: CycleSettings.PreHeat,
  }));

  const loadRecipes = useCallback(async () => {
    setIsLoading(true);
    await getRecipes();
    setIsLoading(false);
  }, [getRecipes]);

  const onDeleteClickHandler = (
    id: string,
    payload: (typeof postWeldViewValues)[number] | (typeof preHeatViewValues)[number],
  ) => {
    setDeleteRecipeId(id);
    setDeleteRecipeType(payload.type);
  };

  const onCloseModal = () => {
    setDeleteRecipeId(null);
    setDeleteRecipeType(null);
  };

  const onAcceptModal = useCallback(async () => {
    const recipeToDelete = recipes.find((recipe) => recipe.id === deleteRecipeId);
    await removeRecipe(recipeToDelete.id);

    // if (deleteRecipeType === CycleSettings.PostWeld) {
    //   await removeRecipe(recipes[deleteRecipeIndex].id);
    // } else {
    //   await removePreHeatRecipe(preHeatRecipes[deleteRecipeIndex].id);
    // }
    loadRecipes();
    onCloseModal();
  }, [removeRecipe, loadRecipes, recipes, deleteRecipeId, deleteRecipeType]);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  const actionsButtons: IActionsDropdown[] = [
    {
      icon: () => (
        <Tooltip hint={t('commonText.edit')}>
          <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (id: string) => onEditClickHandler(id),
      loading: false,
      tag: IActionsTag.Link,
    },
    {
      icon: () => (
        <Tooltip hint={t('commonText.delete')} hintClassName="bottom-7 right-[-120%]" isCustomSettings>
          <IconBin fill="inherit" iconClassName="text-inherit dark:text-white" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: onDeleteClickHandler,
    },
  ];

  const getRecipeToDelete = () => {
    const recipe = recipes.find((recipe) => recipe.id === deleteRecipeId);

    return recipe?.name;
  };

  return (
    <div className="p-recipes-view flex h-full w-full flex-col">
      <div className="mb-5 flex w-full items-center justify-between">
        <h2 className="text-title-md2 font-semibold text-black dark:text-white">Recipes</h2>
        <InteractiveButton text="Create Recipe" onClick={onCreateClickHandler} />
      </div>
      <div className="mb-5 flex w-full items-center gap-4">
        <div className="flex-1 ">
          <InputControl
            placeholder="Search recipes by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      {isLoading ? (
        <div className="flex w-full justify-center">
          <SpinnerLoader />
        </div>
      ) : (
        <>
          <div className="mb-8">
            <h3 className="mb-4 text-lg font-semibold text-black dark:text-white">Post Weld Recipes</h3>
            <Table
              values={postWeldViewValues}
              defaultKeys={['Name']}
              actionsButtons={actionsButtons}
              defaultFieldSort="Name"
            />
          </div>
          <div>
            <h3 className="mb-4 text-lg font-semibold text-black dark:text-white">Pre Heat Recipes</h3>
            <Table
              values={preHeatViewValues}
              defaultKeys={['name', 'rate (°C/h)', 'finalTemp (°C)', 'soakUpper (°C)', 'soakLower (°C)']}
              actionsButtons={actionsButtons}
              defaultFieldSort="name"
            />
          </div>
          <Modal
            modalTitle="Recipe Deletion"
            isVisible={!!deleteRecipeId}
            content={`Are you sure you want to delete the '${getRecipeToDelete()}' recipe?`}
            acceptBtnText="Delete"
            onClose={onCloseModal}
            onAccept={onAcceptModal}
            showAcceptBtn
            showCancelBtn
          />
        </>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  recipes: state.recipes.recipes as Recipe[],
  // preHeatRecipes: state.pre_heat_recipes.preHeatRecipes as PreHeatRecipe[],
});

const mapDispatch = (dispatch: Dispatch) => ({
  getRecipes: dispatch.recipes.getRecipes,
  getPreHeatRecipes: dispatch.pre_heat_recipes.getPreHeatRecipes,
  removeRecipe: dispatch.recipes.removeRecipe,
  removePreHeatRecipe: dispatch.pre_heat_recipes.removePreHeatRecipe,
});

export default connect(mapState, mapDispatch)(RecipesView);
